"use client"

import { Button } from "@/elements"
import { decodeGraphqlNodeId } from "@/tracking/decodeGqlNodeToId"
import debounce from "lodash/debounce"
import { PostAJobSteps } from "./usePostAJobModal"
import { ampli } from "@/ampli"
import { useFormContext } from "react-hook-form"

interface Props {
  step: number
  onNext: () => void
  onBack: () => void
  createAndDraftJobPost: () => void
  createAndPublishJobPost: () => void
  updatePostedJob: () => void
  onDone: () => void
  isSubmitting: boolean
  isCreatingDraft: boolean
  loading?: boolean
  isEditingPostedJob: boolean
  handleInviteCreatives: () => void
  selectedCreativeIdsToInvite: string[]
  isMutatingInviteCreatives: boolean
  jobPostingId: string | null | undefined
}

export const BottomNav: React.FC<Props> = ({
  step,
  onNext,
  onBack,
  onDone,
  createAndPublishJobPost,
  createAndDraftJobPost,
  updatePostedJob,
  isSubmitting,
  isCreatingDraft,
  isEditingPostedJob,
  loading = false,
  selectedCreativeIdsToInvite,
  handleInviteCreatives,
  isMutatingInviteCreatives = false,
  jobPostingId,
}) => {
  // Debounced versions of onClick handlers
  const debouncedOnNext = debounce(onNext, 300)
  const debouncedOnDone = debounce(onDone, 300)
  const debouncedCreateAndPublishOrEdit = debounce(isEditingPostedJob ? updatePostedJob : createAndPublishJobPost, 300)
  const debouncedCreateAndDraft = debounce(createAndDraftJobPost, 300)

  const hasBackSteps = [1, 2, 3]

  if (step === PostAJobSteps.Success) {
    return null
  }

  return (
    <div className="flex h-[72px] flex-row items-center justify-between border-t border-bg-3 px-4 sm:px-6 md:pl-5 md:pr-10">
      {/* Back Button */}
      {hasBackSteps.includes(step) ? (
        <Button disabled={loading} onClick={onBack} variant="white">
          Back
        </Button>
      ) : (
        <div />
      )}

      {/* Right-Side Buttons */}
      <div className="flex flex-row items-center">
        {/* Skip (only in step 4) */}
        {step === PostAJobSteps.InviteCreatives && (
          <Button
            disabled={loading}
            onClick={() => {
              ampli.inviteToJobExit({
                entry_point: "JobPostingForm",
                job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
              })
              onNext()
            }}
            variant="white"
            className="mr-3">
            Skip
          </Button>
        )}

        {(step === 1 || step === 2 || step === 3) && !isEditingPostedJob && (
          <Button
            disabled={loading}
            onClick={debouncedCreateAndDraft}
            loading={isCreatingDraft}
            className="mr-2"
            variant="white">
            Save draft
          </Button>
        )}
        {/* Conditional rendering of Next, Submit, or Done buttons based on the current step */}
        {step === 1 && <Button onClick={debouncedOnNext}>Next</Button>}

        {step === 2 && <Button onClick={debouncedOnNext}>Next</Button>}

        {step === 3 && (
          <Button onClick={debouncedCreateAndPublishOrEdit} loading={isSubmitting}>
            {isEditingPostedJob ? "Save changes" : "Post job"}
          </Button>
        )}

        {step === PostAJobSteps.InviteCreatives && (
          <Button disabled={loading} onClick={handleInviteCreatives} loading={isMutatingInviteCreatives}>
            Send invites{selectedCreativeIdsToInvite.length > 0 ? ` (${selectedCreativeIdsToInvite.length})` : ""}
          </Button>
        )}
      </div>
    </div>
  )
}
